import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * SE2 · V-cart-04 (2026-10-06): the controls a cart row offers, in the order a focus that has lost its own control
 * falls back through. A plan button is matched by its plan, so focus returns to the half that was pressed.
 */
const ROW_CONTROLS = [
  '.cart-items__remove',
  '.quantity-minus',
  '.quantity-plus',
  'cart-quantity-selector-component input',
  '.cart-items__title',
  '.cart-items__media-container',
];

/**
 * The selector that finds `control` again in a re-rendered row.
 * @param {Element} control
 * @returns {string}
 */
function controlSelector(control) {
  if (control instanceof HTMLElement && control.matches('[data-se2-plan]')) {
    return `[data-se2-plan="${CSS.escape(control.dataset.se2Plan ?? '')}"]`;
  }
  return ROW_CONTROLS.find((selector) => control.matches(selector)) ?? '';
}

/**
 * V-cart-09: the keys of the cart lines a grouped row stands for (data-se2-group-keys, snippets/cart-products),
 * its own first; empty for a row that stands for one line.
 * @param {HTMLElement} row
 * @returns {string[]}
 */
function groupKeysOf(row) {
  return (row.dataset.se2GroupKeys || '').split(',').filter(Boolean);
}

/**
 * Whether focus can land on `element` (connected, rendered, not disabled, not inside a hidden or leaving row).
 * @param {Element | null} element
 * @returns {element is HTMLElement}
 */
function canFocus(element) {
  return (
    element instanceof HTMLElement &&
    element.isConnected &&
    !(/** @type {any} */ (element).disabled) &&
    element.getClientRects().length > 0 &&
    !element.closest('[hidden], .removing')
  );
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      this.#changePending = false;
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );

  /**
   * V-cart-09 (fix round 1, 2026-10-06): checkout posts #cart-form, which carries every line's quantity. A stepper
   * change reaches the cart only after the 300 ms debounce, the request and the re-render; until then the form can
   * hold a split the cart is about to replace. These track that window so checkout can wait it out (#onSubmit).
   */
  #changePending = false;
  /** @type {Set<Promise<unknown>>} */
  #inFlight = new Set();
  #holdingSubmit = false;
  #lastUpdateFailed = false;
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * V-cart-04: where focus was when a cart update began, so it can be put back if the re-render takes its control
   * away (a plan switch changes the line's key, a remove takes the row).
   * @type {{ key: string | null, index: number, control: string, at: number } | null}
   */
  #focusMemo = null;

  /**
   * The quantity each line was last sent at, until the re-render that follows shows the server's own (V-cart-01:
   * Enter commits a quantity and the blur after it would otherwise send the same one again).
   * @type {Map<string, number>}
   */
  #sentQuantities = new Map();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('submit', this.#onSubmit);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityInput);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityInput);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * SE2 · V-cart-01 (2026-10-06): Enter in a line's quantity field commits that quantity here, as a blur would.
   * Without this the browser submitted #cart-form through its default button, which was checkout, and Shopify
   * redirected to checkout instead of updating the line. snippets/cart-products also gives the form a hidden
   * "update" default button for when this script is missing.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;
    const selector = input.closest('cart-quantity-selector-component');
    if (!selector || !this.contains(selector)) return;

    event.preventDefault();
    /** @type {any} */ (selector).setQuantity?.(event);
  };

  /**
   * V-cart-09 (fix round 1): runs at once on every stepper change in this cart, before the debounce. It marks a
   * change as pending, and for a grouped row it writes into the form what /cart/update.js will be sent (see
   * #mirrorGroup).
   * @param {Event} event
   */
  #onQuantityInput = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    this.#changePending = true;
    const row = this.refs.cartItemRows[line - 1];
    if (row) this.#mirrorGroup(row, quantity);
  };

  /**
   * V-cart-09 (fix round 1): every line of a grouped row posts its own quantity from a hidden updates[] input
   * (snippets/cart-products), and only the re-render updates those. From the moment the row's stepper changes, the
   * first line's input holds the new total and the others 0, exactly what #updateQuantity sends to /cart/update.js,
   * so a checkout pressed before the re-render posts the shopper's quantity, not the old split. The values the
   * server rendered are kept in data-se2-posted and put back if the change fails (#restoreGroup); a re-render
   * replaces both.
   * @param {HTMLElement} row
   * @param {number} quantity
   */
  #mirrorGroup(row, quantity) {
    const keys = groupKeysOf(row);
    if (keys.length < 2) return;
    if (quantity === Number(row.dataset.se2Qty)) {
      this.#restoreGroup(row);
      return;
    }
    keys.forEach((key, index) => {
      const input = this.#postedInput(key);
      if (!input) return;
      if (input.dataset.se2Posted === undefined) input.dataset.se2Posted = input.value;
      input.value = String(index === 0 ? quantity : 0);
    });
  }

  /**
   * Puts a grouped row's posted quantities back to what the server rendered.
   * @param {HTMLElement | undefined} row
   */
  #restoreGroup(row) {
    if (!row) return;
    for (const key of groupKeysOf(row)) {
      const input = this.#postedInput(key);
      if (!input || input.dataset.se2Posted === undefined) continue;
      input.value = input.dataset.se2Posted;
      delete input.dataset.se2Posted;
    }
  }

  /**
   * The input that posts a line's quantity with #cart-form: its stepper, or the hidden input that stands in for it.
   * @param {string} key
   * @returns {HTMLInputElement | null}
   */
  #postedInput(key) {
    const input = this.querySelector(`.cart-items__table-row[data-key="${CSS.escape(key)}"] input[name="updates[]"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * V-cart-09 (fix round 1): checkout waits while a quantity change is pending or in flight, then submits again with
   * the same button once the cart has settled and the form holds what the cart holds. If the change was refused it
   * does not go on: the line says why, and the shopper decides. Only #cart-form is held; other forms pass.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    if (!this.#changePending && this.#inFlight.size === 0) return;

    event.preventDefault();
    if (this.#holdingSubmit) return;
    this.#holdingSubmit = true;
    const submitterId = event.submitter instanceof HTMLElement ? event.submitter.id : '';

    this.#settled().then((ok) => {
      this.#holdingSubmit = false;
      if (!ok) return;
      const freshForm = document.getElementById('cart-form');
      if (!(freshForm instanceof HTMLFormElement) || !this.contains(freshForm)) return;
      const submitter = submitterId ? document.getElementById(submitterId) : null;
      if (submitter instanceof HTMLButtonElement && submitter.form === freshForm) {
        freshForm.requestSubmit(submitter);
      } else {
        freshForm.requestSubmit();
      }
    });
  };

  /**
   * Resolves once no quantity change is pending or in flight (at most 8 s), with false if the last one failed.
   * @returns {Promise<boolean>}
   */
  async #settled() {
    const until = Date.now() + 8000;
    while ((this.#changePending || this.#inFlight.size > 0) && Date.now() < until) {
      if (this.#inFlight.size > 0) {
        const timeLeft = new Promise((resolve) => setTimeout(resolve, Math.max(0, until - Date.now())));
        await Promise.race([Promise.allSettled([...this.#inFlight]), timeLeft]);
      } else {
        // The debounce has not fired yet; a timer, not a microtask, so it can.
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }
    return !this.#lastUpdateFailed;
  }

  /**
   * Keeps `work` in the in-flight set until it settles.
   * @param {Promise<unknown>} work
   */
  #track(work) {
    this.#inFlight.add(work);
    const done = () => this.#inFlight.delete(work);
    work.then(done, done);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Nothing to change: the line already holds this quantity (or was just sent it). The row's data-se2-qty is the
    // quantity its stepper shows, a grouped row's total included (snippets/cart-products).
    const row = this.refs.cartItemRows[line - 1];
    const key = row?.dataset.key ?? '';
    const current = this.#sentQuantities.has(key) ? this.#sentQuantities.get(key) : Number(row?.dataset.se2Qty);
    if (quantity > 0 && quantity === current) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    // V-cart-09: a grouped row takes the hidden lines it stands for with it.
    const groupKeys = groupKeysOf(cartItemRowToRemove);

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ...this.refs.cartItemRows.filter(
        (row) => row !== cartItemRowToRemove && groupKeys.includes(row.dataset.key ?? '')
      ),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      // The SE2 drawer keeps its head (the title and close) in both states, so the empty template carries no heading
      // of its own; the existing heading is only dropped when the template brings a replacement (Horizon default).
      const cloneHasHeading = clone.querySelector('#cart-drawer-heading') !== null;

      startViewTransition(() => {
        if (cloneHasHeading) document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        // V-cart-04: the last remove button left with its row; focus goes to the cart heading.
        this.#restoreFocus();
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => {
        row.remove();
        // V-cart-04: if focus left with the row, the next row's control takes it now, not after the re-render.
        this.#restoreFocus();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#rememberFocus();
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const updatedRow = this.refs.cartItemRows[line - 1];
    const lineId = updatedRow?.dataset.key ?? '';
    const sections = Array.from(sectionsToUpdate).join(',');
    const sectionsUrl = window.location.pathname;
    this.#lastUpdateFailed = false;

    // V-cart-09: a grouped row (several cart lines of one product, split by a discount) changes as one. The first
    // line takes the new total and the others go to 0 in a single /cart/update.js keyed by line; Shopify sums the
    // lines and re-splits them (checked on DEV 2026-10-06). Removing the row sends 0 for every line.
    const groupKeys = updatedRow ? groupKeysOf(updatedRow) : [];
    const grouped = groupKeys.length > 1;
    /** @type {Record<string, number>} */
    const groupUpdates = {};
    groupKeys.forEach((key, index) => (groupUpdates[key] = index === 0 ? quantity : 0));

    const body = JSON.stringify(
      grouped
        ? { updates: groupUpdates, sections, sections_url: sectionsUrl }
        : { line: line, quantity: quantity, sections, sections_url: sectionsUrl }
    );
    const url = grouped ? Theme.routes.cart_update_url || '/cart/update.js' : Theme.routes.cart_change_url;

    if (lineId) this.#sentQuantities.set(lineId, quantity);

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: grouped
          ? groupKeys.map((key) => ({ id: key, quantity: groupUpdates[key] ?? 0 }))
          : [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    const work = fetch(`${url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // Shopify answers a refused change with `errors`, or with a 4xx status and a `description` (update.js,
        // and change.js on some refusals). Either way the line shows the reason instead of failing silently.
        const errorText =
          parsedResponseText.errors ||
          (Number(parsedResponseText.status) >= 400 && (parsedResponseText.description || parsedResponseText.message));
        if (errorText) {
          this.#sentQuantities.delete(lineId);
          this.#lastUpdateFailed = true;
          deferredUpdatePromise.reject(new Error(String(errorText)));
          return this.#showRefusal({
            line,
            lineId,
            isQuantityChange: config.action === 'change' && quantity > 0,
            description: String(errorText),
          });
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        // The re-render is part of the work checkout waits for (#onSubmit): the form is only right once it lands.
        const rendered = morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        })
          .then(() => this.#restoreFocus())
          .catch((error) => console.warn('[cart-items] Re-render failed:', error))
          .finally(() => this.#sentQuantities.delete(lineId));

        this.#updateCartQuantitySelectorButtonStates();
        return rendered;
      })
      .catch((error) => {
        console.error(error);
        this.#sentQuantities.delete(lineId);
        this.#lastUpdateFailed = true;
        // Nothing came back to re-render from: the row goes back to what the server last rendered.
        this.#resetRow(updatedRow);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });

    this.#track(work);
  }

  /**
   * Puts a row's stepper and posted quantities back to what the server last rendered.
   * @param {HTMLElement | undefined} row
   */
  #resetRow(row) {
    if (!row) return;
    const input = row.querySelector('cart-quantity-selector-component input');
    if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    this.#restoreGroup(row);
  }

  /**
   * V-cart-06 (fix round 1, 2026-10-06): Shopify refused a change. Its sentence is written for adds ("Only 1 item was
   * added to your cart due to availability.") and a refused change can still leave the line at what is in stock, so
   * the section is rendered again first and the line then says, in the theme's words, what the cart holds now:
   * data-se2-qty-error on the row's error cell (snippets/cart-products), printed with the line's quantity. A refused
   * remove, or a re-render that fails, keeps Shopify's reason.
   * @param {{ line: number, lineId: string, isQuantityChange: boolean, description: string }} refusal
   */
  async #showRefusal({ line, lineId, isQuantityChange, description }) {
    let rendered = true;
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
      });
    } catch (error) {
      rendered = false;
      if (/** @type {any} */ (error)?.name !== 'AbortError') console.warn('[cart-items] Re-render failed:', error);
    }

    const rows = Array.from(this.querySelectorAll('.cart-items__table-row[data-key]')).filter(
      (row) => row instanceof HTMLElement
    );
    const row = /** @type {HTMLElement | undefined} */ (
      rows.find((candidate) => candidate instanceof HTMLElement && candidate.dataset.key === lineId) ?? rows[line - 1]
    );
    if (!rendered) this.#resetRow(row);
    this.#restoreFocus();

    const cell = row?.querySelector('.cart-items__error');
    const themeText = cell instanceof HTMLElement ? cell.dataset.se2QtyError : '';
    const message = isQuantityChange && rendered && themeText ? themeText : description;
    this.#showLineError(row, message);
  }

  /**
   * Shows `message` in a row's error cell. The text is written a beat after the cell shows, so the role="alert"
   * inside it is announced with its text, and the cell is then scrolled into view; in the drawer the scroller's
   * scroll padding keeps it clear of the pinned checkout bar (snippets/cart-drawer, V-cart-08).
   * @param {HTMLElement | undefined} row
   * @param {string} message
   */
  #showLineError(row, message) {
    const cell = row?.querySelector('.cart-items__error');
    const text = row?.querySelector('.cart-item__error-text');
    if (cell instanceof HTMLElement && text instanceof HTMLElement) {
      text.textContent = '';
      cell.classList.remove('hidden');
      setTimeout(() => {
        text.textContent = message;
        cell.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
      }, 50);
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: message || 'Cart update failed',
        code: 'INVALID',
      })
    );
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    // V-cart-04: an update started from a control in this cart (the plan toggle dispatches from document) is
    // remembered now, while that control still has focus.
    this.#rememberFocus();

    // V-cart-09 (fix round 1): a change made outside this component (the plan toggle, an add) also holds checkout
    // until its re-render lands, so the form posts the lines as they now are.
    if (!event.promise) return;
    this.#lastUpdateFailed = false;
    const work = event.promise
      .then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            this.#restoreFocus();
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          return sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.#lastUpdateFailed = true;
        console.warn('[cart-items] Event promise rejected:', error);
      });
    this.#track(work);
  };

  /**
   * V-cart-04 (2026-10-06): note where focus is before a cart update re-renders this component. Only focus inside
   * this component counts; a cart change made from elsewhere (a product page add) leaves focus alone.
   */
  #rememberFocus() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) {
      this.#focusMemo = null;
      return;
    }
    const row = active.closest('.cart-items__table-row[data-key]');
    const rows = this.#visibleRows();
    this.#focusMemo = {
      key: row instanceof HTMLElement ? row.dataset.key ?? null : null,
      index: row instanceof HTMLElement ? rows.indexOf(row) : -1,
      control: controlSelector(active),
      at: Date.now(),
    };
  }

  /**
   * V-cart-04: after a re-render, give focus back if the update took it away (focus fell to <body> or left with a
   * removed row). In order: the same control in the same line; in the line now at its place (a plan switch gives the
   * line a new key, a remove moves the next line up) or the one before; another control of that row; the cart
   * heading (the drawer's, or the /cart H1, given tabindex -1). In the modal drawer that keeps focus in the dialog.
   */
  #restoreFocus() {
    const memo = this.#focusMemo;
    if (!memo || Date.now() - memo.at > 10000) return;

    const active = document.activeElement;
    const lost = !active || active === document.body || !active.isConnected || Boolean(active.closest('.removing'));
    if (!lost) return;

    const rows = this.#visibleRows();
    const row =
      rows.find((candidate) => candidate.dataset.key === memo.key) ??
      (memo.index >= 0 ? rows[memo.index] ?? rows[memo.index - 1] : undefined) ??
      null;

    /** @type {HTMLElement | null} */
    let target = null;
    if (row) {
      for (const selector of [memo.control, ...ROW_CONTROLS]) {
        const candidate = selector ? row.querySelector(selector) : null;
        if (canFocus(candidate)) {
          target = candidate;
          break;
        }
      }
    }

    if (!target) {
      const heading = this.isDrawer
        ? document.getElementById('cart-drawer-heading')
        : this.querySelector('.cart-title h1, h1');
      if (heading instanceof HTMLElement) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        target = heading;
      }
    }

    if (!target) return;
    target.focus();
    // Remember the new place, so a second re-render (the server's, after an optimistic remove, which in the drawer
    // re-renders the heading too) can put focus back again. On the heading the memo keeps no row, so it lands there.
    if (target.closest('.cart-items__table-row')) {
      this.#rememberFocus();
    } else {
      this.#focusMemo = { key: null, index: -1, control: '', at: Date.now() };
    }
  }

  /** @returns {HTMLElement[]} The rows a shopper sees: not hidden group lines, not rows on their way out. */
  #visibleRows() {
    return Array.from(this.querySelectorAll('.cart-items__table-row[data-key]')).filter(
      (row) => row instanceof HTMLElement && !row.hidden && !row.classList.contains('removing')
    );
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
